import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { CheckCircle, Clock, AlertTriangle } from 'lucide-react';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { apiClient } = useAuth();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  const checkPaymentStatus = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/api/payments/checkout-status/${sessionId}`);
      if (data.payment_status === 'paid') {
        setStatus('success');
        toast.success('Payment successful!');
      } else if (attempts < 5) {
        setTimeout(() => setAttempts((a) => a + 1), 2000);
      } else {
        setStatus('pending');
      }
    } catch {
      setStatus('error');
    }
  }, [apiClient, sessionId, attempts]);

  useEffect(() => {
    if (sessionId) checkPaymentStatus();
  }, [sessionId, checkPaymentStatus]);

  return (
    <div className="min-h-screen pt-16 flex items-center justify-center px-6 bg-[var(--bg-alt)]" data-testid="payment-success-page">
      <div className="max-w-md w-full card-elevated p-8 text-center">
        {status === 'checking' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-[var(--primary-blue)] mx-auto mb-6" />
            <h2 className="text-2xl font-heading font-bold mb-2">Verifying Payment...</h2>
            <p className="text-[var(--text-body)]">Please wait</p>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
            <h2 className="text-3xl font-heading font-bold mb-2 text-green-600">Payment Successful!</h2>
            <p className="text-[var(--text-body)] mb-8">Thank you for your purchase.</p>
            <Link to="/dashboard" className="btn-primary inline-block" data-testid="back-to-dashboard-btn">Back to Dashboard</Link>
          </>
        )}
        {status === 'pending' && (
          <>
            <Clock size={48} className="text-yellow-500 mx-auto mb-4" />
            <h2 className="text-2xl font-heading font-bold mb-2">Processing...</h2>
            <p className="text-[var(--text-body)] mb-8">You'll receive a confirmation email.</p>
            <Link to="/dashboard" className="btn-outline inline-block">Dashboard</Link>
          </>
        )}
        {status === 'error' && (
          <>
            <AlertTriangle size={48} className="text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-heading font-bold mb-2 text-red-600">Error</h2>
            <p className="text-[var(--text-body)] mb-8">Could not verify payment. Contact support if charged.</p>
            <Link to="/dashboard" className="btn-outline inline-block">Dashboard</Link>
          </>
        )}
      </div>
    </div>
  );
}
